<script setup>
    import Popper from 'vue3-popper';
    import { ref, reactive, onMounted, watch, onBeforeUpdate } from 'vue';
    import InputField from '@/components/client/Inputs/Input';

    const props = defineProps({
        couponType: [Number, null],
        couponCode: [String, File, null],
        campaignType: String,
        editMode: Boolean,
        errors: Object,
    });

    const emits = defineEmits(['update:couponType', 'update:couponCode']);

    const types = ref([
        {
            title: 'Vanlig rabattkod',
            desc: 'Vanligast! Detta är den kodtyp som nästan alla använder sig av. Du väljer själv vad koden ska vara. Det kan t.ex. vara "Juli-FAMILJ". Koden är inte begränsad i antal och kan användas obegränsat antal gånger. Det är lika vanligt att e-handlare, som butiker och restauranger använder denna kodtyp. Vid e-handelskampanjer kommer koden att kunna kopieras för att sedan klistras in på er websida. För fysiska butiker, restauranger mm kommer koden dyka upp på medlemskortet i mobilen för att av medlem sedan visas upp i kassan.',
            value: 3,
            forCampaignTypes: ['local', 'webshop'],
        },
        {
            title: 'Rabattkod visas 1 gång per Medlem',
            desc: 'Denna kod innebär att erbjudandet endast exponeras en gång per medlem. När en medlem klickar på "Visa kod" så kommer den att vara synlig för medlemmen i 24 timmar. Klickar medlemmen på erbjudandet igen, när 24 timmar har gått, så möts hen av ett meddelande att erbjudandet redan har utnyttjats. Ofta används denna kodtyp om det är ett introduktionserbjudande. T.ex: "30% rabatt på första köpet hos Familjekortet!"',
            value: 1,
            forCampaignTypes: ['local', 'webshop'],
        },
        {
            title: 'Kampanj utan rabattkod',
            desc: 'Denna kodtyp används när du har ett erbjudande som inte kan använda en kod. Det kan t.ex. vara i de fall ni marknadsför en tävling, eller för er som jobbar med välgörenhet.',
            value: 4,
            forCampaignTypes: ['webshop', 'local'],
        },
        {
            title: 'QR/Streck-kod',
            desc: 'Denna kodtyp väljer ni om ni vill använda en QR, eller en streckkod, som medlemmen sedan kan scanna i er kassa. Smidigt och snabbt för både kunden och hen i kassan!',
            value: 5,
            forCampaignTypes: ['local', 'webshop'],
        },
    ]);

    const selectedType = ref(props.couponType);
    const _couponCode = ref(props.couponCode);

    import ImageUpload from '@/components/client/ImageUpload';
    import SecondaryButton from '@/components/client/Buttons/SecondaryButton';
    const barcodeImage = ref(null);
    const barcodeImageUploadRed = ref();

    const refs = ref([]);

    onBeforeUpdate(() => {
        refs.value = [];
    });

    function selectType(type) {
        if (selectedType.value === type.value || props.editMode) {
            return;
        }

        refs.value.forEach((ref) => {
            ref?.hide();
        });

        selectedType.value = type.value;
        emits('update:couponType', selectedType.value);

        _couponCode.value = null;
        barcodeImage.value = null;
        emits('update:couponCode', null);
    }

    function couponCodeUpdated() {
        emits('update:couponCode', _couponCode.value);
    }

    watch(() => props.campaignType, (currentValue, oldValue) => {
        // const firstType = types.value.filter(t => t.forCampaignTypes.includes(currentValue))[0];

        // selectType(firstType);
    });

    function onBarcodeImageUploaded(image, file) {
        barcodeImage.value = image;
        emits('update:couponCode', file);
    }

    onMounted(() => {
        if (props.couponType === 5) {
            barcodeImage.value = props.couponCode;
        }
    })
</script>

<template>
   <div :class="{ 'flex flex-col md:flex-row mb-6': editMode }">
        <image-upload
            ref="barcodeImageUploadRed"
            @image-selected="onBarcodeImageUploaded" />

        <div class="mb-4 md:mb-0 md:mx-2" :class="{ 'md:w-1/3': editMode, 'mb-4 w-80 flex-none': !editMode }">
            <h2 class="text-2xl mb-2 text-red font-averta">Välj typ av kampanjkod</h2>

            <p class="text-sm text-gray font-avertaLight">Här väljer du vilken typ av kampanjkod kampanjen ska ha. Dvs. hur koden ska fungera och hur den ska användas. </p>
        </div>

        <div class="md:mx-2 rounded-md" :class="{ 'md:w-2/3': editMode }">
            <div class="flex flex-col rounded-md border-2 border-blue-800 border-opacity-20">
                <div
                    v-for="type in types.filter(t => t.forCampaignTypes.includes(campaignType))"
                    @click="selectType(type)"
                    class="w-full flex px-4 py-4 cursor-pointer relative"
                    :class="{
                        'bg-blue': selectedType === type.value,
                        'text-gray': selectedType !== type.value,
                        'cursor-not-allowed': (editMode && selectedType !== type.value),
                    }">

                    <svg v-if="selectedType === type.value" xmlns="http://www.w3.org/2000/svg" class="absolute text-green-800 top-3 right-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>

                    <div v-if="selectedType !== type.value" class="h-5 w-5 border-2 border-green rounded-full absolute top-3 right-3"></div>

                    <svg v-if="type.value === 3" xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>

                    <svg v-if="type.value === 1" xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                    </svg>

                    <svg v-if="type.value === 4" xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>

                    <svg v-if="type.value === 5" xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 flex-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>

                    <div class="ml-4 w-full">
                        <div class="text-xl flex items-center">
                            <span class="font-averta text-gray">{{ type.title }}</span>
                            <!-- <span
                                v-if="type.forCampaignTypes.length === 1 && type.forCampaignTypes[0] === 'webshop'"
                                class="bg-secondaryBlue text-white px-3 py-[2px] rounded-full text-xs ml-2">E-Handel</span> -->
                        </div>

                        <div class="text-sm"><expandable-text :ref="el => { refs.push(el) }" :max-length="100" class="font-avertaLight text-gray">{{ type.desc }}</expandable-text></div>

                        <div class="mt-2" v-if="type.value === 3 && selectedType === type.value">
                            <input-field
                                v-model="_couponCode"
                                :error="errors.coupon_code"
                                label="Rabattkod"
                                @input="couponCodeUpdated"
                                name="coupon"
                                placeholder="*Ange rabattkod" />
                        </div>

                        <div class="mt-2" v-if="type.value === 1 && selectedType === type.value">
                            <input-field
                                v-model="_couponCode"
                                label="Rabattkod"
                                @input="couponCodeUpdated"
                                name="coupon"
                                placeholder="*Ange rabattkod" />
                        </div>

                        <div class="mt-2" v-if="type.value === 4 && selectedType === type.value">
                            <input-field
                                v-model="_couponCode"
                                label="Extern URL"
                                @input="couponCodeUpdated"
                                name="coupon"
                                placeholder="*Ange extern URL" />
                        </div>

                        <div class="mt-2 w-full" v-if="type.value === 5 && selectedType === type.value">
                            <div v-if="!barcodeImage" class="border py-12 border-dotted border-blue-900 flex justify-center w-full rounded-md">
                                <secondary-button
                                    @click="barcodeImageUploadRed.triggerFileInputDialog()">
                                    Ladda upp bild
                                </secondary-button>
                            </div>

                            <div v-if="barcodeImage" class="text-center">
                                <img :src="barcodeImage">
                                <div class="mt-2 hover:cursor-pointer underline" @click="barcodeImageUploadRed.triggerFileInputDialog()">(Ändra bild)</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
